import { useEffect } from "react";
import { Form, Row, Col, Select, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";

export default function EconomicBehaviour({
  onFinish,
  initialValues,
  form,

  // ✅ Options coming from template
  interestStructureOptions,
  couponTypeOptions,
  amortisationTypeOptions,
  principalRepaymentTypeOptions,
  incomeRecognitionMethodOptions,
  accrualPostingFrequencyOptions,
  couponFrequencyOptions,
  bondCouponFrequencies,
  productCategory,
}) {
  const isFixedIncome = productCategory === 1;

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  return (
    <div>
      <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
        Behaviour & Instrument Rules
      </h3>

      <Form layout="vertical" form={form} onFinish={onFinish}>
        {/* Row 1: Interest Structure + Coupon Type (Coupon Type only for Fixed Income) */}
        <Row>
          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Interest Structure
                  <Tooltip title="Selects the interest computation engine and defines how income is generated.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="interestStructure"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={interestStructureOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Amortisation Type
                  <Tooltip title="Controls how principal is repaid over the life of the instrument.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="amortisationType"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={amortisationTypeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Principal Repayment Type
                  <Tooltip title="Defines how and when principal repayment events are generated.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="principalRepaymentType"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={principalRepaymentTypeOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Income Recognition Method
                  <Tooltip title="Determines how income is recognised in P&L over time.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="incomeRecognitionMethod"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={incomeRecognitionMethodOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: Accrual Required & Accrual Frequency — always shown */}
        <Row>
          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Accrual Required
                  <Tooltip title="Enables or disables accrual processing.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="accrualRequiredFlag"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={[
                  { label: "Yes", value: true },
                  { label: "No", value: false },
                ]}
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Accrual Frequency
                  <Tooltip title="Controls accrual batch posting frequency.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="accrualFrequency"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={accrualPostingFrequencyOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {isFixedIncome && (
          <Row>
            <Col span={12} style={{ padding: "0 8px" }}>
              <Form.Item
                label={
                  <span>
                    Coupon Frequency
                    <Tooltip title="Defines the periodicity of coupon cashflows and accrual cut-offs.">
                      <InfoCircleOutlined
                        style={{ marginLeft: 4, color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="couponFrequency"
              >
                <Select
                  style={{ height: 44 }}
                  suffixIcon={<ChevronDown color="#667085" />}
                  options={bondCouponFrequencies}
                />
              </Form.Item>
            </Col>

            <Col span={12} style={{ padding: "0 8px" }}>
              <Form.Item
                label={
                  <span>
                    Coupon Type
                    <Tooltip title="Determines whether coupon schedules are generated and whether reset logic is required.">
                      <InfoCircleOutlined
                        style={{ marginLeft: 4, color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="couponType"
              >
                <Select
                  style={{ height: 44 }}
                  suffixIcon={<ChevronDown color="#667085" />}
                  options={couponTypeOptions}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        <button type="submit" hidden />
      </Form>
    </div>
  );
}
